import { FC } from 'react';
import { Box, Button, Grid, GridProps, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { Link } from 'react-router-dom';
import { useTranslation } from 'mir-utils/i18n';
import { FeedbackProps, useFeedback } from '../providers';
import { LoadingButton } from './LoadingButton';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    buttonMainAction: {
      marginRight: theme.spacing(3),
    },
  })
);

type Props = {
  pathForCancelAction: string;
  pathForMainAction: string;
  textMainButton: string;
  textCancelButton: string;
  onClickMainAction: () => void;
  onClickCancelAction: () => void;
  disableMainActionButton: boolean;
  disableCancelActionButton: boolean;
  hideCancelButton?: boolean;
  feedbackProps: FeedbackProps;
  isLoading: boolean;
};

/**
 * @summary Кнопки типа "Сохранить" и "Отмена".
 *
 * @param pathForCancelAction - URI для перехода из формы назад. В этом случае
 * кнопка отмены работает, как ссылка.
 * @param pathForMainAction - URI для перехода из формы после её подтверждения. В этом случае
 * кнопка подтверждения работает, как ссылка.
 * @param textMainButton - Текст внутри кнопки подтверждения. По умолчанию "Сохранить".
 * @param textCancelButton - Текст внутри кнопки отмены. По умолчанию "Отмена".
 * @param onClickMainAction - Коллбэк при нажатии на кнопку подтверждения.
 * @param onClickCancelAction - Коллбэк при нажатии на кнопку отмены.
 * @param disableMainActionButton - Флаг того, что кнопка подтверждения заблокирована.
 * @param disableCancelActionButton - Флаг того, что кнопка отмены заблокирована.
 * @param feedbackProps - Cвойства для фидбэка.
 * @param hideCancelButton - признак скрытия кнопки "Отменить" .
 * @param isLoading - Флаг того, что внутри кнопки подтверждения будет отображаться индикатор
 * загрузки(LoadingButton).
 *
 * @description - По умолчанию используется кнопка LoadingButton и при отправлении формы в formik
 * появляется крутилка.
 *
 * @todo Сделать так, чтобы фидбэк(если он нужен) менялся в зависимости от результата выполнения действия.
 *
 * @see FeedbackComponent
 * @see LoadingButton
 */
export const SaveAndCancelButtons: FC<Partial<Props & GridProps>> = (props) => {
  const {
    pathForCancelAction,
    pathForMainAction,
    textMainButton,
    textCancelButton,
    onClickMainAction,
    onClickCancelAction,
    disableMainActionButton,
    disableCancelActionButton,
    feedbackProps,
    isLoading,
    hideCancelButton,
    ...gridProps
  } = props;
  const classes = useStyles({});
  const { showFeedback } = useFeedback();
  const { t } = useTranslation();

  return (
    <Grid container {...gridProps}>
      <Grid item>
        <Box className={classes.buttonMainAction}>
          <LoadingButton
            data-testid="saveAndCancelButtonMainAction"
            type="submit"
            isLoading={isLoading}
            disabled={disableMainActionButton}
            {...((onClickMainAction || feedbackProps) && {
              onClick: async () => {
                onClickMainAction && (await onClickMainAction());
                feedbackProps && showFeedback(feedbackProps);
              },
            })}
            {...(props.pathForMainAction && { component: Link, to: pathForMainAction })}
          >
            {textMainButton || t('Save')}
          </LoadingButton>
        </Box>
      </Grid>
      {!hideCancelButton && (
        <Grid item>
          <Button
            {...(pathForCancelAction && { component: Link, to: pathForCancelAction })}
            {...(onClickCancelAction && { onClick: () => onClickCancelAction() })}
            disabled={disableCancelActionButton || isLoading}
            variant="contained"
          >
            {textCancelButton || t('Cancel')}
          </Button>
        </Grid>
      )}
    </Grid>
  );
};
